import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { tradingAPI, walletAPI, getErrorMessage } from '../../utils/api';

const COIN_NAMES = {
  BTC: 'Bitcoin',
  ETH: 'Ethereum',
  USDT: 'Tether',
  USDC: 'USD Coin',
  BNB: 'BNB',
  XRP: 'XRP',
  SOL: 'Solana',
  ADA: 'Cardano',
  DOGE: 'Dogecoin',
  LTC: 'Litecoin',
  MATIC: 'Polygon',
};

const FIAT_SYMBOLS = { GBP: '£', USD: '$', EUR: '€' };
const FIAT_CURRENCY = 'USD';
const FIAT_DISPLAY = FIAT_SYMBOLS[FIAT_CURRENCY] || FIAT_CURRENCY;

const PRICE_POLL_MS = 30000;

export default function BuyCrypto() {
  const navigate = useNavigate();

  const [wallets, setWallets] = useState([]);
  const [prices, setPrices] = useState({});
  const [selectedAsset, setSelectedAsset] = useState('BTC');
  const [fiatAmount, setFiatAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  const pollRef = useRef(null);

  const fetchWallets = async () => {
    try {
      const res = await walletAPI.getAll();
      setWallets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load wallets'));
    }
  };

  const fetchPrices = async () => {
    try {
      const res = await tradingAPI.getPrices();
      const data = Array.isArray(res.data) ? res.data : [];
      const map = {};
      data.forEach((coin) => {
        if (coin.symbol) map[coin.symbol.toUpperCase()] = coin.current_price;
      });
      setPrices(map);
    } catch {
      // silently keep stale prices
    }
  };

  useEffect(() => {
    const init = async () => {
      setPageLoading(true);
      await Promise.all([fetchWallets(), fetchPrices()]);
      setPageLoading(false);
    };
    init();

    pollRef.current = setInterval(fetchPrices, PRICE_POLL_MS);
    return () => clearInterval(pollRef.current);
  }, []);

  // Derived values
  const cryptoWallets = wallets.filter(
    (w) => w.asset_symbol !== 'USD' && w.asset_symbol !== 'GBP' && w.asset_symbol !== 'EUR'
  );
  const gbpWallet = wallets.find((w) => w.asset_symbol === FIAT_CURRENCY);
  const gbpBalance = gbpWallet?.available_balance ?? 0;
  const hasUSDWallet = wallets.some(
    (w) => w.asset_symbol === 'USD' && (w.available_balance || 0) > 0
  );

  const amount = parseFloat(fiatAmount) || 0;
  const currentPrice = prices[selectedAsset] || 0;
  const cryptoAmount = currentPrice > 0 && amount > 0 ? amount / currentPrice : 0;
  const shortfall = amount - gbpBalance;
  const hasInsufficient = amount > 0 && amount > gbpBalance;
  const canBuy =
    amount >= 1 && currentPrice > 0 && selectedAsset && !hasInsufficient && !loading;

  const handleBuy = async () => {
    setLoading(true);
    try {
      const res = await tradingAPI.buyCrypto({
        asset_symbol: selectedAsset,
        fiat_amount: amount,
        fiat_currency: FIAT_CURRENCY,
      });
      setResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Purchase failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setFiatAmount('');
    fetchWallets();
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Buy Crypto</h1>
          <p className="text-gray-500 mt-1">Purchase crypto instantly from your balance</p>
        </div>

        {/* USD balance pill */}
        <div className="mb-4">
          <span className="inline-block bg-gray-100 rounded-lg px-3 py-1 text-sm text-gray-600">
            Available balance:{' '}
            <span className="font-semibold text-gray-800">
              {FIAT_DISPLAY}{gbpBalance.toFixed(2)}
            </span>
          </span>
        </div>

        {/* Main card */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          {result ? (
            /* ── Success state ── */
            <SuccessView result={result} onReset={handleReset} onViewWallet={() => navigate('/user/wallets')} />
          ) : (
            /* ── Purchase form ── */
            <>
              {cryptoWallets.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-6">
                  No wallets assigned yet. Contact support to get started.
                </p>
              ) : (
                <>
                  {!hasUSDWallet && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-4">
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        No USD balance found. Please contact support to fund your USD account before buying crypto.
                      </p>
                    </div>
                  )}
                  {/* Asset selector */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Asset
                    </label>
                    <select
                      value={selectedAsset}
                      onChange={(e) => setSelectedAsset(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {cryptoWallets.map((w) => (
                        <option key={w.asset_symbol} value={w.asset_symbol}>
                          {w.asset_symbol} — {COIN_NAMES[w.asset_symbol] || w.asset_symbol}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Amount input */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount ({FIAT_CURRENCY})
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="0.01"
                      value={fiatAmount}
                      onChange={(e) => setFiatAmount(e.target.value)}
                      placeholder="Enter amount in USD"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-400 mt-1">Minimum purchase: {FIAT_DISPLAY}1.00</p>
                  </div>

                  {/* Live preview */}
                  {amount > 0 && currentPrice > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4 mt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">You will receive</span>
                        <span className="font-bold text-gray-900">
                          {cryptoAmount.toFixed(8)} {selectedAsset}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Current price</span>
                        <span className="text-gray-700">
                          {FIAT_DISPLAY}{currentPrice.toLocaleString()} per {selectedAsset}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Available balance</span>
                        <span className="text-gray-700">{FIAT_DISPLAY}{gbpBalance.toFixed(2)}</span>
                      </div>

                      {hasInsufficient && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                          <p className="text-red-600 text-sm">
                            Insufficient balance. You need {FIAT_DISPLAY}
                            {shortfall.toFixed(2)} more.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Buy button */}
                  <button
                    onClick={handleBuy}
                    disabled={!canBuy}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-xl mt-4 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading
                      ? 'Processing…'
                      : cryptoAmount > 0
                      ? `Buy ${cryptoAmount.toFixed(6)} ${selectedAsset}`
                      : `Buy ${selectedAsset}`}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SuccessView({ result, onReset, onViewWallet }) {
  return (
    <div className="text-center">
      <style>{`
        @keyframes buy-pop {
          0%   { transform: scale(0); opacity: 0; }
          70%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); }
        }
      `}</style>

      {/* Animated checkmark */}
      <div
        className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center"
        style={{ animation: 'buy-pop 0.4s ease-out forwards' }}
      >
        <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h2 className="text-xl font-bold text-gray-900 mb-1">Purchase Successful!</h2>
      <p className="text-gray-500 text-sm mb-5">Your order has been completed.</p>

      {/* Details */}
      <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2 mb-6">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Amount</span>
          <span className="font-semibold text-gray-900">
            {result.crypto_amount} {result.asset_symbol}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Paid</span>
          <span className="font-semibold text-gray-900">
            {FIAT_DISPLAY}{result.fiat_amount.toFixed ? result.fiat_amount.toFixed(2) : result.fiat_amount}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Rate</span>
          <span className="text-gray-700">
            {FIAT_DISPLAY}{result.price_at_purchase.toLocaleString()} per {result.asset_symbol}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Order ID</span>
          <span className="font-mono text-gray-500 text-xs">{result.order_id}</span>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onReset}
          className="flex-1 border border-gray-200 text-gray-700 font-medium py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
        >
          Buy More
        </button>
        <button
          onClick={onViewWallet}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-xl transition-colors"
        >
          View Wallet
        </button>
      </div>
    </div>
  );
}
